import range from 'lodash/range';
import flatMap from 'lodash/flatMap';
import round from 'lodash/round';
export const DEFAULT_DTI_PERCENTAGE = 0.28;
export const BUYING_POWER_SLIDER_MIDDLE_VALUE = 0.35;
export const BUYING_POWER_SLIDER_SPECS = {
  min: 0.2,
  max: 0.5,
  step: 0.01,
};
const DEFAULT_DTI_RANGE = { min: 0.2, max: 0.5 };

const COMFORTABLE_DTI_MAX_PERCENTAGE = 0.36;
const STRETCHING_DTI_MAX_PERCENTAGE = 0.43;

export const isComfortableDti = (dtiPercentage) =>
  dtiPercentage <= COMFORTABLE_DTI_MAX_PERCENTAGE;
export const isStretchingDti = (dtiPercentage) =>
  dtiPercentage > COMFORTABLE_DTI_MAX_PERCENTAGE &&
  dtiPercentage <= STRETCHING_DTI_MAX_PERCENTAGE;
export const isAggresiveDti = (dtiPercentage) =>
  dtiPercentage > STRETCHING_DTI_MAX_PERCENTAGE;

/**
 * [CALCULATION UTILs]
 */
/**
 * Calculate monthly mortgage payment
 * Reference:
 * - https://ojolabs.atlassian.net/wiki/spaces/CN/pages/1476493609/Financial+Readiness+Design+Document#Buying-power-calculation%3A-Deep-dive
 * - https://www.wikihow.com/Calculate-Mortgage-Payments
 *
 */
 const getMonthlyMortgagePayment = (
  monthlyRate,
  numberOfPayments,
  loanAmount
) => {
  const aprFactor = Math.pow(monthlyRate + 1, numberOfPayments);
  const numerator = loanAmount * (aprFactor * monthlyRate);
  const denominator = aprFactor - 1;

  return numerator / denominator;
};


/**
 * Calculate the monthly payment section - home insurnce & property tax scale with homePrice, while PMI rate scales with loan amount.
 * Monthly payment does not include user's monthly debt input
 * Reference:
 * - https://ojolabs.atlassian.net/wiki/spaces/CN/pages/1476493609/Financial+Readiness+Design+Document#Buying-power-calculation%3A-Deep-dive
 */
 export const getMonthlyPaymentInfo = (
  homePrice,
  downpayment,
  monthlyRates
) => {
  const {
    monthlyMortgageRate,
    monthlyHomeInsuranceRate,
    monthlyPMIRate,
    monthlyPropertyTaxRate,
  } = monthlyRates;
  const loanAmount = Math.max(0, homePrice - downpayment);
  const mortgage = getMonthlyMortgagePayment(
    monthlyMortgageRate,
    360,
    loanAmount
  );
  const pmi = homePrice > downpayment * 5 ? monthlyPMIRate * loanAmount : 0;
  const propertyTax = homePrice * monthlyPropertyTaxRate;
  const insurance = homePrice * monthlyHomeInsuranceRate;
  return {
    monthlyMortgagePayment: mortgage,
    monthlyPMIPayment: pmi,
    monthlyPropertyTaxPayment: propertyTax,
    monthlyHomeInsurancePayment: insurance,
    total: mortgage + pmi + propertyTax + insurance,
  };
};


export const computeBuyingPowerWithPMI = (
    input
  ) => {
    const {
      monthlyMortgageRate,
      numOfMortgagePayments,
      monthlyPMIRate,
      monthlyPropertyTaxRate,
      monthlyHomeInsuranceRate,
      monthlyIncome,
      monthlyDebt,
      downpayment,
      dti,
    } = input;

    const aprFactor = Math.pow(1 + monthlyMortgageRate, numOfMortgagePayments);

    const numerator =
      (dti * monthlyIncome - monthlyDebt) * (aprFactor - 1) +
      downpayment * (aprFactor * monthlyMortgageRate) +
      downpayment * (monthlyPMIRate * (aprFactor - 1));
    const denominator =
      aprFactor * monthlyMortgageRate +
      (monthlyPMIRate + monthlyHomeInsuranceRate + monthlyPropertyTaxRate) *
        (aprFactor - 1);

    return numerator / denominator;
  };

  export const computeBuyingPowerWithoutPMI = (
    input
  ) => {
    const {
      monthlyMortgageRate,
      numOfMortgagePayments,
      monthlyPropertyTaxRate,
      monthlyHomeInsuranceRate,
      monthlyIncome,
      monthlyDebt,
      downpayment,
      dti,
    } = input;

    const aprFactor = Math.pow(1 + monthlyMortgageRate, numOfMortgagePayments);

    const numerator =
      (dti * monthlyIncome - monthlyDebt) * (aprFactor - 1) +
      downpayment * (aprFactor * monthlyMortgageRate);
    const denominator =
      aprFactor * monthlyMortgageRate +
      (monthlyHomeInsuranceRate + monthlyPropertyTaxRate) * (aprFactor - 1);

    return numerator / denominator;
  };


  export const computeDtiForHomePrice = (
    homePrice,
    input
  ) => {
    const {
      monthlyMortgageRate,
      monthlyPropertyTaxRate,
      monthlyPMIRate,
      monthlyHomeInsuranceRate,
      monthlyIncome,
      monthlyDebt,
      downpayment,
    } = input;

    const monthlyPaymentInfo = getMonthlyPaymentInfo(homePrice, downpayment, {
      monthlyMortgageRate,
      monthlyHomeInsuranceRate,
      monthlyPropertyTaxRate,
      monthlyPMIRate,
    });
    const totalMonthlyPayment = monthlyPaymentInfo.total + monthlyDebt;

    return totalMonthlyPayment / monthlyIncome;
  };


  export const computeBuyingPowerResultSet = (
    inputSet,
    requestedDtiRange = DEFAULT_DTI_RANGE
  ) => {
    const {
      monthlyMortgageRate,
      monthlyHomeInsuranceRate,
      monthlyPropertyTaxRate,
      monthlyPMIRate,
      downpayment,
    } = inputSet;

    const monthlyRatesData = {
      monthlyMortgageRate,
      monthlyHomeInsuranceRate,
      monthlyPropertyTaxRate,
      monthlyPMIRate,
    };

    // Only pay PMI if downpayment is less than 20%
    const minPriceToStartPayingPMI = downpayment * 5;

    const maxDtiBeforeIncludePMI = Math.ceil(
      computeDtiForHomePrice(minPriceToStartPayingPMI, inputSet),
      2
    );
    const minDtiToIncludePMI = Math.ceil(
      computeDtiForHomePrice(minPriceToStartPayingPMI + 1, inputSet),
      2
    );

    const hasWithPmiDtiRangeOnly = maxDtiBeforeIncludePMI < requestedDtiRange.min;
    const hasWithoutPmiDtiRangeOnly =
      maxDtiBeforeIncludePMI >= requestedDtiRange.max;

    const withoutPmiDtiRange = (() => {
      if (hasWithPmiDtiRangeOnly) {
        return null;
      } else if (hasWithoutPmiDtiRangeOnly) {
        return requestedDtiRange;
      } else {
        return {
          min: requestedDtiRange.min,
          max: maxDtiBeforeIncludePMI,
        };
      }
    })();

    const withPmiDtiRange = (() => {
      if (hasWithPmiDtiRangeOnly) {
        return requestedDtiRange;
      } else if (hasWithoutPmiDtiRangeOnly) {
        return null;
      } else {
        return {
          min: minDtiToIncludePMI,
          max: requestedDtiRange.max,
        };
      }
    })();

    const transformRangeToBuyingPowerRange = (
      dtiRange,
      computeFn
    ) => {
      if (!dtiRange) {
        return null;
      }
      const { min, max } = dtiRange;

      const minTotal = Math.max(
        //  We want to keep the minPriceToStartPayingPMI is a special breakpoint value (between not paying PMI and start paying PMI)
        0,
        min === maxDtiBeforeIncludePMI
          ? minPriceToStartPayingPMI
          : computeFn({ ...inputSet, dti: min })
      );
      const minMonthly = getMonthlyPaymentInfo(
        minTotal,
        downpayment,
        monthlyRatesData
      ).total;

      const maxTotal = Math.max(
        0,
        max === maxDtiBeforeIncludePMI
          ? minPriceToStartPayingPMI
          : computeFn({ ...inputSet, dti: max })
      );
      const maxMonthly = getMonthlyPaymentInfo(
        maxTotal,
        downpayment,
        monthlyRatesData
      ).total;
      return {
        total: {
          min: round(minTotal),
          max: round(maxTotal),
        },
        monthly: {
          min: round(minMonthly),
          max: round(maxMonthly),
        },
        dtiRange,
      };
    };
    return {
      withPMI: transformRangeToBuyingPowerRange(
        withPmiDtiRange,
        computeBuyingPowerWithPMI
      ),
      withoutPMI: transformRangeToBuyingPowerRange(
        withoutPmiDtiRange,
        computeBuyingPowerWithoutPMI
      ),
    };
  };


  export const convertToBuyingPowerResultSetWithGaps = (
    buyingPowerResultSet
  ) => {
    const { withPMI, withoutPMI } = buyingPowerResultSet;
    const returnValWithoutGap = { withPMI, withoutPMI, gapRange: null };
    if (!withPMI || !withoutPMI) {
      return returnValWithoutGap;
    }
    // This should be smaller or equal
    const withoutPMIMax = withoutPMI.dtiRange.max;
    // This should be higher or equal
    const withPMIMin = withPMI.dtiRange.min;

    const numOfStepBetweenRanges = round((withPMIMin - withoutPMIMax) * 100);
    if (numOfStepBetweenRanges > 1) {
      const totalDiffPerStep = round(
        (withPMI.total.min - withoutPMI.total.max) / numOfStepBetweenRanges
      );
      const monthlyDiffPerStep = round(
        (withPMI.monthly.min - withoutPMI.monthly.max) / numOfStepBetweenRanges
      );
      return {
        withPMI,
        withoutPMI,
        gapRange: {
          total: {
            min: withoutPMI.total.max + totalDiffPerStep,
            max: withPMI.total.min - totalDiffPerStep,
          },
          monthly: {
            min: withoutPMI.monthly.max + monthlyDiffPerStep,
            max: withPMI.monthly.min - monthlyDiffPerStep,
          },
          dtiRange: {
            min: round(withoutPMIMax + 0.01, 2),
            max: round(withPMIMin - 0.01, 2),
          },
        },
      };
    }

    return returnValWithoutGap;
  };

/**
 * Transform result set with potential gap into a flat array of BuyingPowerAtDtiData to use for quickly look up.
 * The function expands each of the range using linear distance between the min / max divided by the number of steps between that min / max;
 */
 export const convertToBuyingPowerAtDti = (
  buyingPowerResults
) => {
  const { withPMI, withoutPMI, gapRange } = buyingPowerResults;
  const nonNullBuyingPowerRanges = [withoutPMI, gapRange, withPMI].filter(
    f => !!f
  );

  if (nonNullBuyingPowerRanges.length === 0) {
    // Both are null
    return [];
  }

  return flatMap(nonNullBuyingPowerRanges, ({ total, monthly, dtiRange }) => {
    const numOfSteps = round((dtiRange.max - dtiRange.min) * 100);
    const totalDiffPerStep = round((total.max - total.min) / numOfSteps);
    const monthlyDiffPerStep = round((monthly.max - monthly.min) / numOfSteps);

    const minDtiPercentage = round(dtiRange.min * 100);
    const maxDtiPercentage = round(dtiRange.max * 100);
    return range(minDtiPercentage, maxDtiPercentage + 1, 1).map(
      (dtiPercentage, index) => ({
        dti: round(dtiPercentage / 100, 2),
        total:
          // For withoutPMI range, the max is a pretty number (downpayment * 5), so this condition helps keeping that pretty number,
          // but it maintains the logic for all other cases as well
          dtiPercentage === maxDtiPercentage
            ? total.max
            : total.min + index * totalDiffPerStep,
        monthly:
          dtiPercentage === maxDtiPercentage
            ? monthly.max
            : monthly.min + index * monthlyDiffPerStep,
      })
    );
  });
};


/**
 * Quick function to be used in slider-concerned components to get the expanded list of buying power data for each DTI percentage
 */
 export const normalizeBuyingPowerResultsData = (
  buyingPowerResults
) => {
  if (buyingPowerResults.withPMI || buyingPowerResults.withoutPMI) {
    return convertToBuyingPowerAtDti(
      convertToBuyingPowerResultSetWithGaps(buyingPowerResults)
    );
  }
  return [];
};


